import { useCallback, useMemo } from 'react';
export const useCardLayout = (containerWidth = 1, containerHeight = 1, options = {}) => {
    const { actualCardCount = null, cardAspectRatio = 1, cardCountLimit = Infinity, minCardWidth = 240, padding = 0, zoomLevel = 0 } = options;
    const minCardHeight = minCardWidth / cardAspectRatio;
    // convenience aspect ratio converters for dimensions that include 1x padding
    const heightToWidth = useCallback((height) => (height - padding) * cardAspectRatio + padding, [cardAspectRatio, padding]);
    const widthToHeight = useCallback((width) => (width - padding) / cardAspectRatio + padding, [cardAspectRatio, padding]);
    // padding is added to grid size to cancel out last row and column padding
    let gridMaxWidth = containerWidth + padding;
    let gridMaxHeight = containerHeight + padding;
    const gridCardLimit = Math.max(0, cardCountLimit);
    // calculate all zoom configurations for grid
    const { allZoomSizes, zoomLevelCount } = useMemo(() => {
        if (gridMaxWidth - padding <= 0 || gridMaxHeight - padding <= 0) {
            return { allZoomSizes: [], zoomLevelCount: 0 };
        }
        const colsForRows = (rows) => {
            const colsToFitItems = Math.ceil(gridCardLimit / rows);
            const colsToFitGrid = Math.floor(gridMaxWidth / heightToWidth(gridMaxHeight / rows));
            return Math.max(1, Math.min(colsToFitItems, colsToFitGrid));
        };
        const rowsForCols = (cols) => {
            const rowsToFitItems = Math.ceil(gridCardLimit / cols);
            const rowsToFitGrid = Math.floor(gridMaxHeight / widthToHeight(gridMaxWidth / cols));
            return Math.max(1, Math.min(rowsToFitItems, rowsToFitGrid));
        };
        // start with max zoom level (one or both dimensions = 1)
        const sizes = [{ cols: colsForRows(1), rows: rowsForCols(1) }];
        // eslint-disable-next-line @typescript-eslint/no-shadow
        let zoomLevelCount = 1;
        // add one row or column until size is smaller than minimal
        let minCardSizeReached = false;
        let cardLimitReached = false;
        while (!minCardSizeReached) {
            const [{ cols, rows }] = sizes;
            cardLimitReached = cardLimitReached || cols * rows >= gridCardLimit;
            // decide to add a row or a column by choosing the option resulting in larger card size
            const newZoom = heightToWidth(gridMaxHeight / (rows + 1)) >= gridMaxWidth / (cols + 1)
                ? { cols: Math.max(cols, colsForRows(rows + 1)), rows: rows + 1 }
                : { cols: cols + 1, rows: Math.max(rows, rowsForCols(cols + 1)) };
            minCardSizeReached =
                gridMaxHeight / newZoom.rows - padding < minCardHeight ||
                    gridMaxWidth / newZoom.cols - padding < minCardWidth;
            if (!minCardSizeReached) {
                sizes.unshift(newZoom);
                // after reaching card count limit, stop counting available zoom levels
                // other zoom levels with capacities above the limit are only processed for capacity calculation purposes
                if (!cardLimitReached) {
                    zoomLevelCount += 1;
                }
            }
        }
        return { allZoomSizes: sizes, zoomLevelCount };
    }, [
        gridMaxWidth,
        gridMaxHeight,
        gridCardLimit,
        minCardWidth,
        minCardHeight,
        heightToWidth,
        widthToHeight,
        padding
    ]);
    if (allZoomSizes.length === 0 || cardCountLimit === 0) {
        return {
            cards: [],
            maxPossibleCards: 0,
            maxZoomLevel: 0,
            allZoomSizes: []
        };
    }
    // choose the desired zoom level. Only pick from `zoomLevelCount` of smallest sizes.
    // if `zoomLevel` is larger than `zoomLevelCount`, clamp to max zoom level (last index)
    // (parent component might request zoom level, that doesn't exist any more)
    const zoomLevelIndex = allZoomSizes.length - zoomLevelCount + Math.max(0, Math.min(zoomLevelCount - 1, zoomLevel));
    const { cols: gridCols, rows: gridRows } = allZoomSizes[zoomLevelIndex];
    // calculate card size to fit the grid space
    const cardWidth = Math.min(gridMaxWidth / gridCols, heightToWidth(gridMaxHeight / gridRows));
    const cardHeight = Math.min(gridMaxHeight / gridRows, widthToHeight(gridMaxWidth / gridCols));
    const maxPossibleCards = Math.min(gridCols * gridRows, gridCardLimit);
    const gridCardCount = Math.min(maxPossibleCards, actualCardCount !== null && actualCardCount !== void 0 ? actualCardCount : Infinity);
    // in case we're laying out fewer cards than the grid would fit, find the optimal row x column configuration
    // to have a centered layout. Zoom configurations can be reused for this.
    let finalCols = gridCols;
    for (let i = allZoomSizes.length - 1; i >= 0; i -= 1) {
        const zoomSize = allZoomSizes[i];
        if (zoomSize.cols * zoomSize.rows >= gridCardCount) {
            finalCols = zoomSize.cols;
            break;
        }
    }
    // in some cases the last row isn't filled at all. Be sure to only count non-empty rows:
    const finalRows = Math.ceil(gridCardCount / finalCols);
    const lastRowMissingCount = finalRows * finalCols - gridCardCount;
    const calculatePosition = (index) => {
        const xIndex = index % finalCols;
        const yIndex = Math.floor(index / finalCols);
        let xInset = xIndex * cardWidth;
        const yInset = yIndex * cardHeight;
        // center last row horizontally
        if (yIndex === finalRows - 1) {
            xInset += (cardWidth * lastRowMissingCount) / 2;
        }
        // center cards in grid
        const xWhiteSpace = gridMaxWidth - cardWidth * finalCols;
        const yWhiteSpace = gridMaxHeight - cardHeight * finalRows;
        const top = yInset + yWhiteSpace / 2 + containerHeight - (gridMaxHeight - padding);
        const start = xInset + xWhiteSpace / 2 + containerWidth - (gridMaxWidth - padding);
        return { top, start };
    };
    const gridCards = Array.from(Array(gridCardCount)).map((_e, gridIndex) => (Object.assign(Object.assign({}, calculatePosition(gridIndex)), { width: cardWidth - padding, height: cardHeight - padding, position: {
            x: gridIndex % finalCols,
            y: Math.floor(gridIndex / finalCols)
        } })));
    return {
        cards: gridCards,
        maxPossibleCards,
        maxZoomLevel: zoomLevelCount - 1,
        allZoomSizes,
        currentZoomSizeIndex: zoomLevelIndex
    };
};
